<template>
  <div class="error-boundary-wrapper">
    <slot v-if="!hasError" />
    
    <!-- Error State -->
    <div v-else class="error-boundary-container">
      <Message severity="error" class="w-full">
        <div class="flex flex-col gap-3">
          <h3 class="text-lg font-semibold">Something went wrong</h3>
          <p class="text-gray-700">{{ fallbackMessage || 'An unexpected error occurred. Please try again.' }}</p>
          
          <div class="flex gap-2">
            <Button
              @click="handleTryAgain"
              severity="secondary"
              label="Try Again"
            >
              <template #icon>
                <span class="material-symbols-rounded text-base">refresh</span>
              </template>
            </Button>
          </div>
        </div>
      </Message>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onErrorCaptured } from 'vue'
import Message from 'primevue/message'
import Button from 'primevue/button'

interface Props {
  fallbackMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  fallbackMessage: ''
})

const hasError = ref(false)

// Capture errors from child components
onErrorCaptured((err: Error, instance, info: string) => {
  console.error('ErrorBoundary caught error:', err, info)
  hasError.value = true
  
  // Prevent the error from propagating further
  return false
})

const handleTryAgain = () => {
  hasError.value = false
}
</script>

<style scoped>
.error-boundary-wrapper {
  width: 100%;
  height: 100%;
}

.error-boundary-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 1rem;
}
</style>